import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import api from '../../api/axiosInstance';
import { Loader } from '../../components/loader';
import LayoutNoAuth from './layout';
import { showError } from '../../components/notifications';

const PASSWORD_MIN = 6;
const RECOVERY_ENDPOINT = '/auth/recover-password';
const VERIFY_ENDPOINT = '/auth/recover-password/verify';

export default function RecoveryPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [showPass, setShowPass] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [touched, setTouched] = React.useState({ password: false, confirm: false });

  const passwordError =
    touched.password && password.trim().length < PASSWORD_MIN
      ? `Минимум ${PASSWORD_MIN} символов`
      : '';

  const confirmError =
    touched.confirm && confirm.trim() !== password.trim() ? 'Пароли не совпадают' : '';

  const isValid =
    !!token && password.trim().length >= PASSWORD_MIN && confirm.trim() === password.trim();

  React.useEffect(() => {
    if (!token) navigate('/forgetpassword', { replace: true });
  }, [token, navigate]);

  const verifyQuery = useQuery<{ valid: boolean }>({
    queryKey: ['verify-reset-token', token],
    enabled: !!token,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ valid: boolean }>(VERIFY_ENDPOINT, {
        params: { token },
      });
      return data;
    },
  });

  React.useEffect(() => {
    if (!token) {
      navigate('/forgetpassword', { replace: true });
      return;
    }

    if (verifyQuery.isSuccess && verifyQuery.data?.valid === false) {
      showError('Ссылка недействительна или устарела');
      navigate('/forgetpassword', { replace: true });
    }
  }, [token, verifyQuery.isSuccess, verifyQuery.data?.valid, navigate]);

  const mutation = useMutation({
    mutationFn: async () => {
      await api.post(RECOVERY_ENDPOINT, {
        token,
        password: password.trim(),
      });
    },
    onSuccess: () => {
      toast.success('Пароль успешно обновлен. Войдите с новым паролем.');
      navigate('/login', { replace: true });
    },
    onError: () => {
      toast.error('Не удалось обновить пароль. Возможно, ссылка устарела. Попробуйте еще раз.');
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ password: true, confirm: true });

    if (!token) {
      toast.error('Недействительная ссылка восстановления.');
      return;
    }

    if (!isValid || mutation.isPending) return;
    mutation.mutate();
  };

  const verifying = !!token && verifyQuery.isLoading;
  const tokenInvalid = !!token && verifyQuery.isSuccess && verifyQuery.data?.valid === false;
  const showForm = !!token && !tokenInvalid && !verifying;
  const disabled = verifying || mutation.isPending;

  return (
    <LayoutNoAuth>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: { xs: 2.5, sm: 3 },
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 2,
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
              Новый пароль
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Придумайте новый пароль и повторите его, чтобы вернуться в аккаунт.
            </Typography>
          </Box>

          {verifying && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Loader />
            </Box>
          )}

          {token && tokenInvalid && (
            <Alert severity="error">
              Ссылка недействительна или устарела.
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => navigate('/forgetpassword')}>
                  Запросить новую ссылку
                </Button>
              </Box>
            </Alert>
          )}

          {!token && (
            <Alert severity="error">
              В ссылке отсутствует токен. Запросите новую ссылку для восстановления.
            </Alert>
          )}

          {showForm && (
            <Box component="form" onSubmit={onSubmit}>
              <TextField
                fullWidth
                label="Новый пароль"
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, password: true }))}
                error={!!passwordError}
                helperText={passwordError || ' '}
                autoComplete="new-password"
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPass ? 'Скрыть пароль' : 'Показать пароль'}
                        onClick={() => setShowPass(v => !v)}
                        onMouseDown={e => e.preventDefault()}
                        edge="end"
                        disabled={disabled}
                      >
                        {showPass ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Повторите пароль"
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, confirm: true }))}
                error={!!confirmError}
                helperText={confirmError || ' '}
                autoComplete="new-password"
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                sx={{ mt: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirm ? 'Скрыть пароль' : 'Показать пароль'}
                        onClick={() => setShowConfirm(v => !v)}
                        onMouseDown={e => e.preventDefault()}
                        edge="end"
                        disabled={disabled}
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack spacing={1.25} sx={{ mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!isValid || disabled}
                  size="large"
                >
                  {mutation.isPending ? <Loader /> : 'Сохранить пароль'}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => navigate('/login')}
                  disabled={disabled}
                >
                  Назад ко входу
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </LayoutNoAuth>
  );
}
